import LegalLayout from "./LegalLayout";
import { useTranslation } from "react-i18next";
import { COMPANY } from "@/legal/companyInfo";

const addr = `${COMPANY.address.street}, ${COMPANY.address.zip} ${COMPANY.address.city}, ${COMPANY.address.country}`;

export default function Privacy() {
  const { t } = useTranslation();
  return (
    <LegalLayout
      title="Politique de confidentialité"
      subtitle={`Comment Oresto collecte, utilise et protège vos données personnelles. En vigueur au ${COMPANY.lastUpdated}.`}
    >
      <section>
        <h2>1. Qui est responsable du traitement de vos données ?</h2>
        <p>
          Le responsable du traitement des données personnelles est <strong>{COMPANY.legalName}</strong> ({COMPANY.legalForm}), IDE {COMPANY.ide}, {COMPANY.registry}, dont le siège social est situé au {addr}.
        </p>
        <p>Pour toute question relative à vos données personnelles, écrivez à <a href={`mailto:${COMPANY.dpoEmail}`}>{COMPANY.dpoEmail}</a>.</p>
      </section>

      <section>
        <h2>2. Quelles données collectons-nous ?</h2>
        <p>Oresto collecte uniquement les données strictement nécessaires au fonctionnement du service :</p>
        <h3>Données du compte utilisateur</h3>
        <ul>
          <li>Adresse email (identifiant de connexion) ;</li>
          <li>Mot de passe (jamais stocké en clair : chiffré avec l'algorithme bcrypt) ;</li>
          <li>Nom et prénom du responsable de l'établissement ;</li>
          <li>Date de création du compte et date de dernière connexion.</li>
        </ul>
        <h3>Données de l'établissement</h3>
        <ul>
          <li>Nom du restaurant, adresse, canton ;</li>
          <li>Numéro de TVA (facultatif) ;</li>
          <li>Logo (image téléversée par le Client, facultative).</li>
        </ul>
        <h3>Données d'exploitation</h3>
        <ul>
          <li>Chiffre d'affaires quotidien (sur place / à emporter) ;</li>
          <li>Nombre de couverts ;</li>
          <li>Factures fournisseurs (fournisseur, montant, échéance, catégorie, statut de paiement) et les photos ou PDF que vous scannez ;</li>
          <li>Relevés bancaires que vous importez pour le rapprochement.</li>
        </ul>
        <h3>Données de facturation</h3>
        <ul>
          <li>Formule d'abonnement souscrite (mensuel ou annuel) ;</li>
          <li>Historique des paiements (montant, date, référence Stripe) ;</li>
          <li><strong>Aucune donnée de carte bancaire n'est stockée par Oresto</strong> ; elles sont traitées exclusivement par Stripe.</li>
        </ul>
        <h3>Données techniques</h3>
        <ul>
          <li>Cookie d'authentification sécurisé (httpOnly, Secure, SameSite=None) ;</li>
          <li>Logs techniques limités (adresse IP tronquée en cas d'incident) ;</li>
          <li>Statistiques d'utilisation anonymisées (via notre hébergeur Emergent) ;</li>
          <li>Uniquement si vous les acceptez : cookies de mesure d'audience et de publicité (Google Tag Manager, Google Analytics, Meta Pixel).</li>
        </ul>
      </section>

      <section>
        <h2>3. Pourquoi utilisons-nous ces données ?</h2>
        <table>
          <thead><tr><th>Finalité</th><th>Base légale</th></tr></thead>
          <tbody>
            <tr><td>Fournir le service (compte, saisie, rapports)</td><td>Exécution du contrat</td></tr>
            <tr><td>Facturation et paiement</td><td>Exécution du contrat + obligation légale</td></tr>
            <tr><td>Envoi des emails transactionnels (bienvenue, reçu, mot de passe oublié)</td><td>Exécution du contrat</td></tr>
            <tr><td>Sécurité, prévention de la fraude</td><td>Intérêt légitime</td></tr>
            <tr><td>Support client</td><td>Exécution du contrat</td></tr>
            <tr><td>Amélioration du produit (statistiques anonymes)</td><td>Intérêt légitime</td></tr>
          </tbody>
        </table>
        <p><strong>Oresto ne vend, ne loue et ne partage vos données à aucun tiers à des fins publicitaires ou commerciales.</strong></p>
      </section>

      <section>
        <h2>4. Combien de temps conservons-nous vos données ?</h2>
        <ul>
          <li><strong>Données du compte actif</strong> : pendant toute la durée de l'abonnement.</li>
          <li><strong>Données après résiliation</strong> : conservées 90 jours en lecture seule pour permettre l'export final, puis supprimées.</li>
          <li><strong>Reçus de paiement et données comptables</strong> : conservés 10 ans pour respecter les obligations comptables suisses (art. 958f CO).</li>
          <li><strong>Logs techniques</strong> : supprimés après 30 jours.</li>
          <li><strong>Tokens de réinitialisation de mot de passe</strong> : supprimés automatiquement après 1 heure.</li>
        </ul>
      </section>

      <section>
        <h2>5. Où vos données sont-elles stockées ?</h2>
        <p>L'application Oresto, sa base de données et ses sauvegardes sont hébergées par notre prestataire <strong>Emergent Labs Inc. (États-Unis)</strong>. La base de données est un service MongoDB Atlas situé aux <strong>États-Unis</strong>, chiffré au repos et sauvegardé automatiquement.</p>
        <p>La lecture automatique des factures et relevés (OCR) et l'assistant IA sont traités par <strong>OpenAI (États-Unis)</strong>, via la passerelle de notre hébergeur. Seuls les documents que vous soumettez sont transmis, uniquement pour produire le résultat demandé.</p>
        <p>Vos données sont donc communiquées à l'étranger, notamment aux États-Unis. Ces communications reposent sur des garanties contractuelles prévues par l'art. 16 nLPD (clauses contractuelles types) ou, lorsque le prestataire y est certifié, sur le Swiss-U.S. Data Privacy Framework. Vous pouvez demander des informations sur ces garanties à <a href={`mailto:${COMPANY.dpoEmail}`}>{COMPANY.dpoEmail}</a>.</p>
      </section>

      <section>
        <h2>6. Qui a accès à vos données ?</h2>
        <p>Vos données sont accessibles uniquement :</p>
        <ul>
          <li>par vous-même via votre espace personnel ;</li>
          <li>par votre fiduciaire, si vous lui donnez un accès en lecture seule (révocable à tout moment) ;</li>
          <li>par Oresto, uniquement lorsque nécessaire au support ou à la maintenance, sous obligation de confidentialité ;</li>
          <li>par nos sous-traitants techniques (voir article 7), sous engagement contractuel de confidentialité et de sécurité.</li>
        </ul>
      </section>

      <section>
        <h2>7. Sous-traitants et prestataires</h2>
        <table>
          <thead><tr><th>Prestataire</th><th>Rôle</th><th>Localisation</th></tr></thead>
          <tbody>
            <tr><td>Emergent Labs Inc.</td><td>Hébergement de l'application, base de données (MongoDB Atlas) et sauvegardes</td><td>États-Unis</td></tr>
            <tr><td>OpenAI</td><td>Lecture IA des factures et relevés, assistant IA</td><td>États-Unis</td></tr>
            <tr><td>Stripe Payments Europe, Ltd.</td><td>Traitement des paiements</td><td>Irlande / États-Unis</td></tr>
            <tr><td>Resend, Inc.</td><td>Emails transactionnels</td><td>États-Unis</td></tr>
            <tr data-testid="privacy-swisstopo"><td>{t("privacy.swisstopo_name")}</td><td>{t("privacy.swisstopo_role")}</td><td>{t("privacy.swisstopo_loc")}</td></tr>
            <tr><td>Google (Tag Manager / Analytics) et Meta</td><td>Mesure d'audience, uniquement après votre consentement</td><td>États-Unis / Irlande</td></tr>
          </tbody>
        </table>
        <p data-testid="privacy-swisstopo-note">{t("privacy.swisstopo_note")}</p>
      </section>

      <section>
        <h2>8. Vos droits</h2>
        <p>Conformément à la nLPD suisse (et au RGPD pour les personnes situées dans l'UE), vous disposez à tout moment des droits suivants :</p>
        <ul>
          <li><strong>Droit d'accès</strong> : obtenir une copie de toutes les données que nous détenons vous concernant ;</li>
          <li><strong>Droit de rectification</strong> : corriger toute donnée inexacte ;</li>
          <li><strong>Droit à l'effacement</strong> (« droit à l'oubli ») : demander la suppression de vos données, sous réserve des obligations légales de conservation ;</li>
          <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré (JSON, CSV, PDF) ;</li>
          <li><strong>Droit d'opposition</strong> : vous opposer à certains traitements fondés sur notre intérêt légitime ;</li>
          <li><strong>Droit de limitation</strong> : demander la suspension temporaire d'un traitement ;</li>
          <li><strong>Droit de plainte</strong> auprès du Préposé fédéral à la protection des données (PFPDT — <a href="https://www.edoeb.admin.ch" target="_blank" rel="noreferrer">edoeb.admin.ch</a>).</li>
        </ul>
        <p>Pour exercer l'un de ces droits, écrivez à <a href={`mailto:${COMPANY.dpoEmail}`}>{COMPANY.dpoEmail}</a>. Nous répondons sous 30 jours maximum, gratuitement.</p>
      </section>

      <section>
        <h2>9. Sécurité</h2>
        <p>Nous mettons en œuvre les mesures techniques et organisationnelles suivantes :</p>
        <ul>
          <li>Chiffrement des communications par TLS 1.3 (HTTPS) ;</li>
          <li>Chiffrement des mots de passe par l'algorithme bcrypt (impossible à décrypter) ;</li>
          <li>Cookies d'authentification httpOnly + Secure + SameSite (impossibles à voler par une injection JavaScript) ;</li>
          <li>Sauvegarde régulière de la base de données ;</li>
          <li>Isolation stricte des données de chaque Client (aucun Client ne peut accéder aux données d'un autre) ;</li>
          <li>Contrôle d'accès restreint au personnel technique d'Oresto ;</li>
          <li>Formation et sensibilisation régulière du personnel à la sécurité.</li>
        </ul>
        <p>En cas de violation de données susceptible d'entraîner un risque pour vos droits, Oresto vous notifie sans délai, conformément à l'art. 24 nLPD.</p>
      </section>

      <section>
        <h2>10. Cookies</h2>
        <p>Oresto utilise des cookies techniques nécessaires au fonctionnement (authentification, préférences d'affichage). Ils ne nécessitent pas de consentement.</p>
        <p>Les cookies de mesure d'audience et de publicité (Google Tag Manager et les outils qu'il charge, Meta Pixel) ne sont déposés <strong>qu'après votre accord</strong> via le bandeau cookies. Vous pouvez modifier votre choix à tout moment via le lien « Cookies » en bas de page.</p>
      </section>

      <section>
        <h2>11. Modification de la présente politique</h2>
        <p>La présente politique peut être mise à jour pour refléter les évolutions du service ou de la réglementation. Toute modification substantielle est notifiée par email au moins 30 jours avant son entrée en vigueur. La version en vigueur est toujours accessible à l'adresse <a href="/confidentialite">{COMPANY.website}/confidentialite</a>.</p>
      </section>

      <section>
        <h2>12. Contact</h2>
        <ul>
          <li>Email dédié aux données personnelles : <a href={`mailto:${COMPANY.dpoEmail}`}>{COMPANY.dpoEmail}</a></li>
          <li>Adresse postale : {COMPANY.legalName}, {addr}</li>
        </ul>
      </section>
    </LegalLayout>
  );
}
